import * as THREE from 'three';
import { BasketballHoop } from './BasketballHoop';
import { BasketballDimensions } from '@shared/constants/BasketballDimensions';

export class BasketballCourt {
  private mesh: THREE.Group;
  
  // NBA court dimensions from shared constants
  private courtWidth: number = BasketballDimensions.court.width; 
  private courtLength: number = BasketballDimensions.court.length;
  private borderWidth: number = BasketballDimensions.court.borderWidth;
  private lineWidth: number = BasketballDimensions.court.lineWidth;
  private hoopA: BasketballHoop;
  private hoopB: BasketballHoop;
  
  constructor() {
    this.mesh = new THREE.Group();
    this.createCourt();
  }
  
  // Helper method to create a line as a mesh with proper width
  private createLineMesh(
    startPoint: THREE.Vector3, 
    endPoint: THREE.Vector3, 
    color: number = 0xffffff,
    y: number = 0.015,
    lineWidth: number = this.lineWidth
  ): THREE.Mesh {


    // Calculate direction vector and normalize it
    const direction = new THREE.Vector3().subVectors(endPoint, startPoint).normalize();
    
    // Calculate perpendicular vector (rotate 90 degrees in XZ plane)
    const perpendicular = new THREE.Vector3(-direction.z, 0, direction.x).normalize();
    
    // Calculate the length of the line
    const length = startPoint.distanceTo(endPoint);
    
    // Create rectangle geometry
    const w = Math.min(length, lineWidth);
    const l = Math.max(length, lineWidth);
    const geometry = new THREE.PlaneGeometry(l, w);
    
    // Create material
    const material = new THREE.MeshStandardMaterial({
      color: color,
      roughness: 0.5,
      metalness: 0.1
    });
    
    // Create mesh
    const mesh = new THREE.Mesh(geometry, material);
    
    // Position and rotate the mesh
    // First position at origin
    mesh.position.set(
      (startPoint.x + endPoint.x) / 2, 
      y,
      (startPoint.z + endPoint.z) / 2
    );
    
    // might need to flip due to winding order
    if (endPoint.z < startPoint.z) {
      mesh.rotateZ(Math.PI);      
    }
    
    
    // Calculate rotation angle (arctan of direction vector in XZ plane)
    const angle = Math.atan2(direction.z, direction.x);
    
    // Apply rotation around Y axis
    mesh.rotateY(angle);

    // flat on court
    if (endPoint.z < startPoint.z) {
        mesh.rotateX(Math.PI / 2);
    }
    else {
      mesh.rotateX(-Math.PI / 2); 
    }

    return mesh;
  }
  
  // Helper method to create a curved line mesh
  private createCurvedLineMesh(
    centerPoint: THREE.Vector3,
    radius: number,
    startAngle: number = 0,
    endAngle: number = Math.PI * 2,
    color: number = 0xffffff,
    y: number = 0.015,
    lineWidth: number = this.lineWidth,
    segments: number = 64
  ): THREE.Mesh {
    // Create a ring geometry
    const innerRadius = radius - lineWidth / 2;
    const outerRadius = radius + lineWidth / 2;
    const thetaSegments = segments;
    const phiSegments = 1;
    const thetaStart = startAngle;
    const thetaLength = endAngle - startAngle;
    
    const geometry = new THREE.RingGeometry(
      innerRadius, outerRadius, thetaSegments, phiSegments, thetaStart, thetaLength
    );
    
    // Create material
    const material = new THREE.MeshStandardMaterial({
      color: color,
      roughness: 0.5,
      metalness: 0.1,
      side: THREE.DoubleSide // To ensure visibility from both sides
    });
    
    // Create mesh
    const mesh = new THREE.Mesh(geometry, material);
    
    // Position the mesh
    mesh.position.set(centerPoint.x, y, centerPoint.z);
    mesh.rotateX(-Math.PI / 2); // Rotate to be flat on the court
    
    return mesh;
  }
  
  private createCourt(): void {
    // Create court floor
    const floorGeometry = new THREE.PlaneGeometry(BasketballDimensions.floor.width, BasketballDimensions.floor.length);
    floorGeometry.rotateX(-Math.PI / 2); // Rotate to be horizontal
    
    // Court texture (wood)
    const floorTexture = new THREE.TextureLoader().load('/images/oai_wood_1.png');
    floorTexture.wrapS = THREE.RepeatWrapping;
    floorTexture.wrapT = THREE.RepeatWrapping;
    floorTexture.repeat.set(8, 8); // Adjusted repeat to match the new dimensions
    
    const floorMaterial = new THREE.MeshStandardMaterial({
      map: floorTexture,
      roughness: 0.5,
      metalness: 0.1
    });
    
    const floor = new THREE.Mesh(floorGeometry, floorMaterial);
    floor.receiveShadow = true;
    this.mesh.add(floor);
    
    // Create court lines
    this.createCourtLines();
    
    // Create hoops
    this.createHoops();
  }
  
  private createCourtLines(): void {
    // Create paint areas first so they appear under the lines
    this.createPaintAreas();
    
    // Outer boundary
    const boundaryPoints = [
      new THREE.Vector3(-this.courtWidth/2, 0, -this.courtLength/2),
      new THREE.Vector3(this.courtWidth/2, 0, -this.courtLength/2),
      new THREE.Vector3(this.courtWidth/2, 0, this.courtLength/2),
      new THREE.Vector3(-this.courtWidth/2, 0, this.courtLength/2),
      new THREE.Vector3(-this.courtWidth/2, 0, -this.courtLength/2),
    ];
    
    // Create boundary lines
    for (let i = 0; i < boundaryPoints.length - 1; i++) {
      const line = this.createLineMesh(boundaryPoints[i], boundaryPoints[i+1]);
      this.mesh.add(line);
    }

    // Center line
    const centerStart = new THREE.Vector3(-this.courtWidth/2, 0, 0);
    const centerEnd = new THREE.Vector3(this.courtWidth/2, 0, 0);
    const centerLine = this.createLineMesh(centerStart, centerEnd);
    this.mesh.add(centerLine);
    
    // Center circle
    const centerCircle = this.createCurvedLineMesh(
      new THREE.Vector3(0, 0, 0), // center
      1.83, // 6 feet = 1.83m
      0, // start angle
      Math.PI * 2 // end angle (full circle)
    );
    this.mesh.add(centerCircle);
    
    // Add coaching box lines - baseline to 28ft line
    const coachBoxLength = 8.53; // 28 feet = 8.53m from baseline
    const coachBoxLineLength = 0.9144; // 3 feet = 0.9144m
    const drawCoachingBox = () => {
      // Bottom half coaching boxes (28ft from baseline)
      // Left side coaching box - bottom half
      const leftBoxStart = new THREE.Vector3(-this.courtWidth/2, 0, -this.courtLength/2 + coachBoxLength);
      const leftBoxEnd = new THREE.Vector3(-this.courtWidth/2 + coachBoxLineLength, 0, -this.courtLength/2 + coachBoxLength);
      const leftCoachBox = this.createLineMesh(leftBoxStart, leftBoxEnd);
      this.mesh.add(leftCoachBox);
      
      // Right side coaching box - bottom half
      const rightBoxStart = new THREE.Vector3(this.courtWidth/2, 0, -this.courtLength/2 + coachBoxLength);
      const rightBoxEnd = new THREE.Vector3(this.courtWidth/2 - coachBoxLineLength, 0, -this.courtLength/2 + coachBoxLength);
      const rightCoachBox = this.createLineMesh(rightBoxStart, rightBoxEnd);
      this.mesh.add(rightCoachBox);

      // Top half coaching boxes (28ft from baseline)
      // Left side coaching box - top half
      const leftBox2Start = new THREE.Vector3(-this.courtWidth/2, 0, this.courtLength/2 - coachBoxLength);
      const leftBox2End = new THREE.Vector3(-this.courtWidth/2 + coachBoxLineLength, 0, this.courtLength/2 - coachBoxLength);
      const leftCoachBox2 = this.createLineMesh(leftBox2Start, leftBox2End);
      this.mesh.add(leftCoachBox2);
      
      // Right side coaching box - top half
      const rightBox2Start = new THREE.Vector3(this.courtWidth/2, 0, this.courtLength/2 - coachBoxLength);
      const rightBox2End = new THREE.Vector3(this.courtWidth/2 - coachBoxLineLength, 0, this.courtLength/2 - coachBoxLength);
      const rightCoachBox2 = this.createLineMesh(rightBox2Start, rightBox2End);
      this.mesh.add(rightCoachBox2);
    };
    
    drawCoachingBox();
    
    // Draw three point arcs for both ends
    this.drawThreePointArc(-this.courtLength/2);
    this.drawThreePointArc(this.courtLength/2);
    
    // Draw lanes for both baskets
    this.drawLane(-this.courtLength/2);
    this.drawLane(this.courtLength/2);
  }
  
  private createPaintAreas(): void {
    // Paint material (light blue/gray color with higher opacity for better visibility)
    const paintMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x6490b1, 
      transparent: true,
      opacity: 0.85,
      roughness: 0.5
    });

    // Lane width and length (must match values in drawLane)
    const laneWidth = BasketballDimensions.lane.width;
    const laneLength = BasketballDimensions.lane.length;

    // Create paint for both ends of court
    const createPaint = (z: number) => {
      const paintGeometry = new THREE.PlaneGeometry(laneWidth, laneLength);
      paintGeometry.rotateX(-Math.PI / 2);
      
      const paint = new THREE.Mesh(paintGeometry, paintMaterial);
      
      // Position exactly at the baseline extending into the court
      // For negative z (first basket), we start at z and extend positive
      // For positive z (second basket), we start at z and extend negative
      const zPos = z < 0 ? z + laneLength/2 : z - laneLength/2;
      paint.position.set(0, 0.005, zPos); // Slightly above court to prevent z-fighting
      
      this.mesh.add(paint);
      
      // Add restricted area semicircle
      //this.createRestrictedArea(z);
    };

    // Create paint areas for both baskets
    createPaint(-this.courtLength/2);
    createPaint(this.courtLength/2);
  }
  
  private createRestrictedArea(z: number): void {
    // Restricted area is a 4-foot radius semicircle (matching diagram)
    const radius = 1.22; // 4 feet in meters
    
    // First create the visual surface for the restricted area (slightly different color)
    const restrictedSurfaceMaterial = new THREE.MeshStandardMaterial({
      color: 0x5a82a8, // Darker color for better visibility
      transparent: true,
      opacity: 0.9,
      roughness: 0.5
    });
    
    const restrictedSurfaceGeometry = new THREE.CircleGeometry(radius, 32, 0, Math.PI);
    restrictedSurfaceGeometry.rotateX(-Math.PI / 2);
    
    // Rotate based on which basket we're working with
    if (z > 0) {
      restrictedSurfaceGeometry.rotateZ(Math.PI);
    }
    
    const restrictedSurface = new THREE.Mesh(restrictedSurfaceGeometry, restrictedSurfaceMaterial);
    restrictedSurface.position.set(0, 0.007, z); // Slightly above the court floor but below lines
    this.mesh.add(restrictedSurface);
    
    // Create the line marking as a curved mesh
    const startAngle = z > 0 ? Math.PI : 0;
    const endAngle = z > 0 ? 2 * Math.PI : Math.PI;
    
    const arc = this.createCurvedLineMesh(
      new THREE.Vector3(0, 0, z),
      radius,
      startAngle,
      endAngle
    );
    this.mesh.add(arc);
  }
  
  private drawLane(z: number): void {
    // According to the diagram:
    const laneWidth = BasketballDimensions.lane.width;
    const laneLength = BasketballDimensions.lane.length;
    const direction = z < 0 ? 1 : -1; // Direction from baseline toward center
    
    // Lane outline - left side
    const laneLeftStart = new THREE.Vector3(-laneWidth/2, 0, z);
    const laneLeftEnd = new THREE.Vector3(-laneWidth/2, 0, z + (laneLength * direction));
    const laneLeft = this.createLineMesh(laneLeftStart, laneLeftEnd);
    this.mesh.add(laneLeft);
    
    // Lane outline - right side
    const laneRightStart = new THREE.Vector3(laneWidth/2, 0, z);
    const laneRightEnd = new THREE.Vector3(laneWidth/2, 0, z + (laneLength * direction));
    const laneRight = this.createLineMesh(laneRightStart, laneRightEnd);
    this.mesh.add(laneRight);
    
    // Free throw line
    const ftLineStart = new THREE.Vector3(-laneWidth/2 - this.lineWidth/2, 0, z + (laneLength * direction));
    const ftLineEnd = new THREE.Vector3(laneWidth/2 + this.lineWidth/2, 0, z + (laneLength * direction));
    const ftLine = this.createLineMesh(ftLineStart, ftLineEnd);
    this.mesh.add(ftLine);
    
    // Free throw circle
    const ftCircle = this.createCurvedLineMesh(
      new THREE.Vector3(0, 0, z + (laneLength * direction)),
      1.83, // 6 feet = 1.83m
      0,
      Math.PI * 2
    );
    this.mesh.add(ftCircle);
    
    // Add no-charge semicircle
    const noChargeRadius = 1.22; // 4 feet = 1.22m
    const noChargeDistance = 4.57; // Distance from baseline (15 feet = 4.57m, which is 4' from free throw line)
    const noChargeZ = z + (noChargeDistance * direction);
    
    // For the semicircle, we need to determine which half to draw
    // If we're at the bottom basket (z < 0), we want the semi-circle to face away from the basket
    // If we're at the top basket (z > 0), we want the semi-circle to face toward the basket
    const startAngle = z < 0 ? Math.PI : 0;
    const endAngle = z < 0 ? 2 * Math.PI : Math.PI;
    
    const noCharge = this.createCurvedLineMesh(
      new THREE.Vector3(0, 0, noChargeZ),
      noChargeRadius,
      startAngle,
      endAngle
    );
    //this.mesh.add(noCharge);
    
    // Add hash marks along lane
    const hashMarkCount = 4; // 4 per side
    const hashMarkLength = 0.3; // Slightly longer marks for visibility
    
    for (let i = 1; i <= hashMarkCount; i++) {
      // Position along the lane - evenly spaced
      const markPosition = z + (laneLength / (hashMarkCount + 1) * i * direction);
      
      // Left side hash mark
      const leftHashStart = new THREE.Vector3(-laneWidth/2, 0, markPosition);
      const leftHashEnd = new THREE.Vector3(-laneWidth/2 + hashMarkLength, 0, markPosition);
      const leftHash = this.createLineMesh(leftHashStart, leftHashEnd);
      this.mesh.add(leftHash);
      
      // Right side hash mark
      const rightHashStart = new THREE.Vector3(laneWidth/2, 0, markPosition);
      const rightHashEnd = new THREE.Vector3(laneWidth/2 - hashMarkLength, 0, markPosition);
      const rightHash = this.createLineMesh(rightHashStart, rightHashEnd);
      this.mesh.add(rightHash);
    }
    
    // Add baseline markings - 14' from center (4.27m)
    const baselineMarkWidth = 4.27; // 14 feet = 4.27m
    const baselineStart = new THREE.Vector3(-baselineMarkWidth, 0, z);
    const baselineEnd = new THREE.Vector3(baselineMarkWidth, 0, z);
    const baselineMark = this.createLineMesh(baselineStart, baselineEnd);
    this.mesh.add(baselineMark);
  }
  
  private drawThreePointArc(z: number): void {
    // NBA 3-point arc has radius 23'9" (7.24m) from center of hoop
    const arcRadius = 7.24; // 23'9" = 7.24m
    
    // Direction depends on which basket we're working with
    const direction = z < 0 ? 1 : -1;
    
    // The straight lines extend 14' (4.27m) from the baseline
    const straightLineLength = 4.27; // 14' from baseline
    
    // Rim center is 5'6" (1.68m) from baseline
    const rimDistanceFromBaseline = 1.68; // 5'6" from baseline
    
    // Rim center position
    const rimCenter = new THREE.Vector3(0, 0, z + (rimDistanceFromBaseline * direction));
    
    // The straight lines are 3' (0.91m) from the sidelines
    const distanceFromSideline = 0.91; // 3' from sideline
    
    // Sideline x-coordinates
    const sideLineX = this.courtWidth / 2;
    const leftLineX = -sideLineX + distanceFromSideline;
    const rightLineX = sideLineX - distanceFromSideline;
    
    // Calculate endpoints of the straight lines from the baseline
    const leftBaselinePoint = new THREE.Vector3(
      leftLineX,
      0,
      z
    );
    
    const rightBaselinePoint = new THREE.Vector3(
      rightLineX,
      0,
      z
    );
    
    // Calculate the z coordinate for the end of the straight lines
    const lineEndZ = z + (straightLineLength * direction);
    
    // Calculate the endpoints of the straight lines (where they extend to)
    const leftLineEndpoint = new THREE.Vector3(
      leftLineX,
      0,
      lineEndZ
    );
    
    const rightLineEndpoint = new THREE.Vector3(
      rightLineX,
      0,
      lineEndZ
    );
    
    // Draw the straight lines along the sidelines
    const leftStraightLine = this.createLineMesh(leftBaselinePoint, leftLineEndpoint);
    this.mesh.add(leftStraightLine);
    
    const rightStraightLine = this.createLineMesh(rightBaselinePoint, rightLineEndpoint);
    this.mesh.add(rightStraightLine);
    
    // Find the points where these lines would intersect with the arc if extended
    // We need to calculate vectors from rim center to line endpoints
    const leftVector = new THREE.Vector3(leftLineX - rimCenter.x, 0, lineEndZ - rimCenter.z + this.lineWidth * direction);
    const rightVector = new THREE.Vector3(rightLineX - rimCenter.x, 0, lineEndZ - rimCenter.z + this.lineWidth * direction);
    // Get the direction unit vectors
    leftVector.normalize();
    rightVector.normalize();
    
    // Scale by arc radius to get points on the arc
    const leftArcPoint = new THREE.Vector3(
      rimCenter.x + leftVector.x * arcRadius,
      0,
      rimCenter.z + leftVector.z * arcRadius
    );
    
    const rightArcPoint = new THREE.Vector3(
      rimCenter.x + rightVector.x * arcRadius,
      0,
      rimCenter.z + rightVector.z * arcRadius
    );
    
    // Connect the line endpoints to the arc points
    const leftConnector = this.createLineMesh(leftLineEndpoint, leftArcPoint);
    this.mesh.add(leftConnector);
    
    const rightConnector = this.createLineMesh(rightLineEndpoint, rightArcPoint);
    this.mesh.add(rightConnector);
    
    // Calculate the angles correctly using atan2
    // In Three.js, we need to use atan2(z, x) for the correct angle in the xz plane
    let leftAngle = Math.atan2(rimCenter.z - leftArcPoint.z, rimCenter.x - leftArcPoint.x);
    let rightAngle = Math.atan2(rimCenter.z - rightArcPoint.z, rimCenter.x - rightArcPoint.x);
    
    // Ensure angles are in the correct range (0 to 2π)
    if (leftAngle < 0) leftAngle += 2 * Math.PI;
    if (rightAngle < 0) rightAngle += 2 * Math.PI;
    
    // The arc should connect the endpoints of the straight lines
    // For bottom basket, arc goes from right to left
    // For top basket, arc goes from left to right
    let startAngle, endAngle;
    
    if (z < 0) {
      // Bottom basket
      startAngle = rightAngle;
      endAngle = leftAngle;
    } else {
      // Top basket
      startAngle = leftAngle;
      endAngle = rightAngle;
    }
    
    // Ensure we draw the arc in the correct direction
    const angleDiff = (endAngle - startAngle + 2 * Math.PI) % (2 * Math.PI);
    if (angleDiff > Math.PI) {
      // We need to draw the other way around
      const temp = startAngle;
      startAngle = endAngle;
      endAngle = temp;
      
      // Adjust to ensure we draw the full arc
      if (endAngle < startAngle) {
        endAngle += 2 * Math.PI;
      }
    } else if (endAngle < startAngle) {
      endAngle += 2 * Math.PI;
    }
    
    // Draw the 3-point arc
    const threePointArc = this.createCurvedLineMesh(
      rimCenter,
      arcRadius,
      startAngle,
      endAngle,
      0xffffff,
      0.015,
      this.lineWidth,
      128 // Higher segment count for smoother arc
    );
    
    this.mesh.add(threePointArc);
    
  }
  
  private createHoops(): void {
    // Create home and away baskets using the BasketballHoop class
    this.hoopA = new BasketballHoop(0xff0000);
    this.hoopA.getMesh().scale.set(2,2,2);
    this.hoopA.positionAt(
        0, 
        BasketballDimensions.rim.heightAboveGround, 
        -this.courtLength/2 + 
        BasketballDimensions.hoopSupport.distanceFromBaseline +
        (BasketballDimensions.rim.distanceFromBackboard +
        BasketballDimensions.rim.radius) * BasketballDimensions.scale.x
    );
    this.mesh.add(this.hoopA.getMesh());
    
    this.hoopB = new BasketballHoop(0x0000ff);
    this.hoopB.getMesh().scale.set(2,2,2);
    this.hoopB.positionAt(
        0, 
        BasketballDimensions.rim.heightAboveGround, 
        this.courtLength/2 - 
        BasketballDimensions.hoopSupport.distanceFromBaseline -
        (BasketballDimensions.rim.distanceFromBackboard +
        BasketballDimensions.rim.radius) * BasketballDimensions.scale.x
    );
    // Rotate the away hoop 180 degrees around the Y axis to face the court
    this.hoopB.getMesh().rotation.y = Math.PI;
    this.mesh.add(this.hoopB.getMesh());
  }
  
  public getMesh(): THREE.Group {
    return this.mesh;
  }
  
  public getDimensions(): { width: number; length: number } {
    return {
      width: this.courtWidth,
      length: this.courtLength
    };
  }

  /**
   * Returns the world position of the center of the rim for the specified hoop
   * @param hoop 'A' or 'B' to specify which hoop to target
   * @returns THREE.Vector3 representing the center of the rim in world coordinates
   */
  public getRimPosition(hoopId: 'A' | 'B'): THREE.Vector3 {
    const hoop = hoopId === 'A' ? this.hoopA : this.hoopB;
    const position = hoop.getRimCenterWorldPosition();
    return position.clone();
  }

  /**
   * Triggers the net animation for the specified hoop
   * @param hoopId 'A' or 'B' to specify which hoop to animate
   */
  public triggerNetAnimation(hoopId: 'A' | 'B'): void {
    const hoop = hoopId === 'A' ? this.hoopA : this.hoopB;
    hoop.triggerNetAnimation();
  }

  /**
   * Updates all net animations if any are playing
   * @param currentTime The current time in milliseconds
   */
  public updateNetAnimations(currentTime: number): void {
    this.hoopA.updateNetAnimation(currentTime);
    this.hoopB.updateNetAnimation(currentTime);
  }
} 